import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Section } from './ui';

export type Device = { endpoint: string; addedAt: string };

type State = 'unavailable' | 'off' | 'pending' | 'on' | 'blocked';

/**
 * The service worker that receives the pushed alerts. It may still be
 * installing when the page opens; without one (development) this gives up.
 */
async function pushManager(): Promise<PushManager | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>(resolve => setTimeout(() => resolve(null), 10000)),
  ]);
  return registration?.pushManager ?? null;
}

/**
 * Lets this device receive the alerts under the application's own name, as
 * any other application's notifications.
 */
export function NotificationsAppareil({
  devices,
  onChange,
}: {
  devices: Device[];
  onChange: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>('off');
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  async function read() {
    const manager = await pushManager();
    if (!manager) {
      setState('unavailable');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('blocked');
      return;
    }
    const subscription = await manager.getSubscription();
    setEndpoint(subscription?.endpoint ?? null);
    setState(subscription ? 'on' : 'off');
  }

  useEffect(() => {
    void read();
  }, []);

  async function enable() {
    setState('pending');
    setFailed(false);
    try {
      const manager = await pushManager();
      const permission = await Notification.requestPermission();
      if (!manager || permission !== 'granted') {
        await read();
        return;
      }
      const key = await send('mtxbudget-server', { route: 'push-key' });
      if (!('data' in key)) {
        throw new Error(key.error);
      }
      const subscription = await manager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: (key.data as { publicKey: string }).publicKey,
      });
      const saved = await send('mtxbudget-server', {
        route: 'push-subscribe',
        body: { subscription: subscription.toJSON() },
      });
      if (!('data' in saved)) {
        await subscription.unsubscribe();
        throw new Error(saved.error);
      }
      await onChange();
    } catch {
      setFailed(true);
    }
    await read();
  }

  async function disable() {
    const manager = await pushManager();
    const subscription = await manager?.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await send('mtxbudget-server', {
        route: 'push-unsubscribe',
        body: { endpoint: subscription.endpoint },
      });
      await onChange();
    }
    await read();
  }

  // Subscribed here but unknown to the server: it would never be notified.
  const device = devices.find(d => d.endpoint === endpoint);
  const shown: State = state === 'on' && !device ? 'off' : state;

  const aside = {
    unavailable: t('Indisponibles'),
    off: t('Désactivées'),
    pending: t('Désactivées'),
    on: t('Activées'),
    blocked: t('Bloquées'),
  }[shown];
  const asideColor = {
    unavailable: undefined,
    off: undefined,
    pending: undefined,
    on: theme.noticeTextLight,
    blocked: theme.warningText,
  }[shown];
  const strong = shown === 'on' || shown === 'blocked';

  return (
    <Section
      title={t('Notifications sur cet appareil')}
      aside={
        <Text style={{ color: asideColor, fontWeight: strong ? 600 : 400 }}>
          {aside}
        </Text>
      }
    >
      <View style={{ gap: 12, padding: 16 }}>
        {shown === 'unavailable' && (
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t(
              'Ce navigateur ne permet pas de recevoir les notifications de MTXbudget.',
            )}
          </Text>
        )}

        {shown === 'off' && (
          <>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {t(
                'Recevez les alertes sous le nom MTXbudget, écran verrouillé compris : heures du lundi, plan des 15 jours, manque à venir.',
              )}
            </Text>
            {failed && (
              <Text style={{ fontSize: 13, color: theme.errorText }}>
                {t("L'activation a échoué : réessayez.")}
              </Text>
            )}
            <Button
              variant="primary"
              onPress={() => void enable()}
              style={{ minHeight: 48, fontWeight: 600 }}
            >
              <Trans>Activer les notifications sur cet appareil</Trans>
            </Button>
          </>
        )}

        {shown === 'pending' && (
          <>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {t(
                "Android vous demande l'autorisation : répondez « Autoriser » pour recevoir les alertes sous le nom MTXbudget.",
              )}
            </Text>
            <Button
              variant="primary"
              isDisabled
              style={{ minHeight: 48, fontWeight: 600 }}
            >
              <Trans>Activation en cours…</Trans>
            </Button>
          </>
        )}

        {shown === 'on' && device && (
          <>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {t(
                "Les alertes arrivent sous le nom MTXbudget, écran verrouillé compris. Les toucher ouvre l'écran concerné.",
              )}
            </Text>
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              {t('Appareil inscrit le {{date}} UTC', {
                date: device.addedAt.slice(0, 16).replace('T', ' '),
              })}
            </Text>
            <Button onPress={() => void disable()} style={{ minHeight: 48 }}>
              {t('Désactiver sur cet appareil')}
            </Button>
          </>
        )}

        {shown === 'blocked' && (
          <>
            <Text style={{ fontSize: 13, color: theme.warningText }}>
              {t('Android bloque les notifications de MTXbudget.')}
            </Text>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {t(
                'Pour les rétablir : Paramètres du téléphone › Applications › MTXbudget › Notifications, puis revenez ici.',
              )}
            </Text>
            <Button onPress={() => void read()} style={{ minHeight: 48 }}>
              {t('Réessayer')}
            </Button>
          </>
        )}
      </View>
    </Section>
  );
}
